import { logger } from "@platform/browser-logger";
import { useEffect } from "react";
import { findNewPlansFromBpg } from "@platform/plan-grid-core";

import type { BenefitPlanGroup, RfpCoverage } from "@platform/plan-grid-core";

interface UseBenefitPlanGroupSyncArgs {
  benefitPlanGroups: BenefitPlanGroup[] | undefined;
  getCurrentCoverage: () => RfpCoverage | undefined;
  setBenefitPlanGroups: (bpgs?: BenefitPlanGroup[]) => void;
  updateSelectedCoverage: (updatedCoverage?: RfpCoverage) => void;
}

export const useBenefitPlanGroupSync = ({
  benefitPlanGroups,
  getCurrentCoverage,
  setBenefitPlanGroups,
  updateSelectedCoverage,
}: UseBenefitPlanGroupSyncArgs): void => {
  useEffect(() => {
    setBenefitPlanGroups(benefitPlanGroups);
  }, [benefitPlanGroups, setBenefitPlanGroups]);

  useEffect(() => {
    const bpgData = benefitPlanGroups;
    if (!bpgData?.length) {
      return;
    }

    const currentCoverage = getCurrentCoverage();
    if (!currentCoverage?.benefitPlans) {
      return;
    }

    const existingPlanIds = new Set(
      currentCoverage.benefitPlans
        .map((plan) => plan.id)
        .filter((id): id is string => !!id),
    );

    const newPlans = findNewPlansFromBpg(bpgData, existingPlanIds);

    if (newPlans.length > 0) {
      logger.info(
        `[PlanDesigner] Merging ${newPlans.length} new plan(s) from BPG data into coverage`,
      );
      const mergedCoverage: RfpCoverage = {
        ...currentCoverage,
        benefitPlans: [...currentCoverage.benefitPlans, ...newPlans],
      };
      updateSelectedCoverage(mergedCoverage);
    }
  }, [benefitPlanGroups, getCurrentCoverage, updateSelectedCoverage]);
};
